import { type ReactNode, forwardRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { zOverlay, zModal } from "@/lib/z";
import { cn } from "@/lib/cn";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    style={{ zIndex: zOverlay }}
    className={cn(
      "fixed inset-0 bg-bg-overlay/70 transition-opacity duration-200",
      "data-[state=open]:opacity-100 data-[state=closed]:opacity-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

const contentVariants = cva(
  "fixed left-1/2 top-1/2 flex max-h-[90vh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col " +
    "overflow-hidden rounded-xl border border-border bg-bg-secondary text-fg-primary shadow-xl " +
    // A <form> around the body must pass the height limit down, or it grows to
    // its content, the body never scrolls and the last fields hide under the footer.
    "[&>form]:flex [&>form]:min-h-0 [&>form]:flex-1 [&>form]:flex-col " +
    "transition-[opacity,transform] duration-200 " +
    "data-[state=open]:opacity-100 data-[state=closed]:opacity-0 " +
    "data-[state=open]:scale-100 data-[state=closed]:scale-95",
  {
    variants: {
      size: {
        sm: "max-w-sm",
        md: "max-w-lg",
        lg: "max-w-2xl",
        xl: "max-w-4xl",
      },
    },
    defaultVariants: { size: "md" },
  },
);

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof contentVariants> {
  /** Hide the default close (X) button in the header corner. */
  hideClose?: boolean;
}

export const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, size, hideClose = false, children, onInteractOutside, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      style={{ zIndex: zModal }}
      className={cn(contentVariants({ size }), className)}
      onInteractOutside={(e) => {
        // A toast sits outside every dialog. Pressing it — to close it, or its
        // button — is not clicking away from the dialog (see DrawerContent).
        if ((e.target as Element | null)?.closest?.("[data-toaster]")) e.preventDefault();
        onInteractOutside?.(e);
      }}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close
          className="absolute right-4 top-4 z-20 rounded-md p-1 text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label="Закрыть"
        >
          <X size={18} strokeWidth={2} aria-hidden="true" />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = "DialogContent";

/** Sticky header — stays pinned while the body scrolls. */
export const DialogHeader = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "sticky top-0 z-10 shrink-0 border-b border-border bg-bg-secondary px-6 py-4 pr-12",
      className,
    )}
    {...props}
  />
));
DialogHeader.displayName = "DialogHeader";

/** Sticky footer — action bar pinned to the bottom of the dialog. */
export const DialogFooter = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border bg-bg-secondary px-6 py-4",
      className,
    )}
    {...props}
  />
));
DialogFooter.displayName = "DialogFooter";

/** Scrollable body region. */
export const DialogBody = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex-1 overflow-y-auto px-6 py-5", className)} {...props} />
));
DialogBody.displayName = "DialogBody";

export const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-tight tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("mt-1 text-sm text-fg-muted", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

/* ---------------------------------------------------------------- ConfirmDialog */

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** "danger" styles the confirm button as destructive. */
  tone?: "default" | "danger";
  confirmText?: ReactNode;
  cancelText?: ReactNode;
  /** Called on confirm; may be async — the button shows a spinner while pending. */
  onConfirm: () => void | Promise<void>;
}

/**
 * ConfirmDialog — driven by props (DESIGN-SYSTEM §5, §6.4).
 * For `tone="danger"`, the confirm button uses the danger variant.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  tone = "default",
  confirmText = "Подтвердить",
  cancelText = "Отмена",
  onConfirm,
}: ConfirmDialogProps) {
  const [pending, setPending] = useState(false);

  async function handleConfirm() {
    try {
      setPending(true);
      await onConfirm();
      onOpenChange(false);
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={pending}>
              {cancelText}
            </Button>
          </DialogClose>
          <Button
            variant={tone === "danger" ? "danger" : "default"}
            loading={pending}
            onClick={handleConfirm}
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
ConfirmDialog.displayName = "ConfirmDialog";
